import { useState, useEffect, type ReactNode } from 'react';
import { Link, useLocation, useSearch } from 'wouter';
import { useAuth } from './state';
import { AnnouncementSurface } from './components/AnnouncementSurface';
import { AvatarMenu, StreakIndicator, BrandLogo } from './components';
import { SiteFooter } from './SiteFooter';
import { MarketingNav } from './components/MarketingNav';
import { HomePill } from './components/HomePill';
// (SiteFooter is still used by PublicShell below.)
import { reopenPreferences } from './consent/consentManager';
import { SidebarNav } from './nav/SidebarNav';
import { MobileNav } from './nav/MobileNav';
import { PageTitleBar } from './nav/PageTitleBar';
import { ScrollHint } from './nav/ScrollHint';
import { AddEmailPrompt } from './components/AddEmailPrompt';
import { IdeaBar, IdeaMapSheet } from './ideaScope/IdeaScopeNav';
import { ideaIdFromPath } from './ideaScope/surfaces';
import { useIdeaScopeSync, useIdeaWorkspace } from './ideaScope/store';
import type { NavCtx } from './nav/navConfig';
import './nav/nav.css';
import { analytics } from '@mindstudio-ai/interface';
import { QuickStartIdea, type QuickAdvisor, type CaptureMode } from './components/QuickStartIdea';
import { GuideLayer } from './guide/GuideLayer';

const PHONE_MQ = '(max-width: 767px)';

export function AppShell({
  children,
  // When true, <main> is height-bounded (min-height: 0) so a page can host a
  // full-height internal scroll region (e.g. the session chat's StickToBottom)
  // instead of growing the whole shell. Default false: <main> grows naturally
  // to its content so the footer follows tall flow-content instead of floating
  // up into the middle of it.
  boundMain: boundMainProp = false,
  // 'desktop': bounded on desktop only. On phones the page flows normally, so the
  // tab bar stays and the page scrolls like any other (the Cost & Filing Manager).
  boundOn = 'all',
}: {
  children: ReactNode;
  boundMain?: boolean;
  boundOn?: 'all' | 'desktop';
}) {
  // Phone (<768px): bottom tab bar + Menu sheet instead of the rail. Seeded
  // from the media query on first render so there's no layout flash.
  const [isPhone, setIsPhone] = useState(
    () => typeof window !== 'undefined' && window.matchMedia(PHONE_MQ).matches,
  );
  useEffect(() => {
    const mq = window.matchMedia(PHONE_MQ);
    const apply = () => setIsPhone(mq.matches);
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);
  const boundMain = boundMainProp && !(boundOn === 'desktop' && isPhone);
  const [menuOpen, setMenuOpen] = useState(false);
  const [location] = useLocation();
  // Query string (updates on ?advisor= changes even when the path stays /strategy),
  // so the advisory-board active state tracks which advisor is focused.
  const search = useSearch();
  const advisorParam = new URLSearchParams(search).get('advisor');
  // Present when a Collection (a pinned tag) is the active view, i.e. /ideas?tag=…
  // Used to keep the plain "Idea Capture" row from lighting up alongside the
  // active Collection row (both live at /ideas).
  const collectionTagParam = new URLSearchParams(search).get('tag');
  const profile = useAuth((s) => s.profile);
  const isAdmin = (profile?.roles ?? []).includes('admin');
  const navCtx: NavCtx = { location, advisorParam, collectionTagParam };
  // "+ Capture" (desktop top bar) and the N shortcut. On an advisor's page, the session starts right after.
  const captureHref = location === '/strategy' && advisorParam && ['market', 'patent', 'finance'].includes(advisorParam) ? `/ideas/new?then=${advisorParam}` : '/ideas/new';
  // "+ Capture" and N open the one-box "What's the idea?" dialog, then straight into a session:
  // that advisor's on an advisor page, otherwise the Product Strategist. The full form stays a link inside it.
  const [quickCaptureOpen, setQuickCaptureOpen] = useState(false);
  // "Make it an idea" from the Notes inbox opens the dialog in Idea mode with the note's text.
  const [captureReq, setCaptureReq] = useState<{ mode: CaptureMode; text?: string; fromNoteId?: string } | null>(null);
  useEffect(() => {
    const onOpen = (e: Event) => {
      const d = (e as CustomEvent).detail ?? {};
      setCaptureReq({ mode: d.mode === 'note' ? 'note' : 'idea', text: d.text, fromNoteId: d.fromNoteId });
      setQuickCaptureOpen(true);
    };
    window.addEventListener('ed:open-capture', onOpen);
    return () => window.removeEventListener('ed:open-capture', onOpen);
  }, []);
  // Every plain "Capture an idea" link (dashboard next step, empty states, hand-offs) opens the same
  // one-box dialog as "+ Capture", instead of the full form. The form stays reachable from inside the
  // dialog ("fill in the form instead"), from /ideas/new?then=…, and by typing the address.
  // Attached at once (not after the profile loads), so an early click doesn't slip through to the form.
  useEffect(() => {
    const onClick = (e: MouseEvent) => {
      if (e.defaultPrevented || e.button !== 0 || e.metaKey || e.ctrlKey || e.shiftKey || e.altKey) return;
      const a = (e.target as HTMLElement | null)?.closest?.('a[href]') as HTMLAnchorElement | null;
      if (!a || a.target === '_blank') return;
      const url = new URL(a.href, window.location.href);
      if (url.origin !== window.location.origin || url.search || !/\/ideas\/new\/?$/.test(url.pathname)) return;
      e.preventDefault();
      e.stopPropagation();
      analytics.track('capture_open', { source: 'link' });
      setCaptureReq({ mode: 'idea' });
      setQuickCaptureOpen(true);
    };
    document.addEventListener('click', onClick, true);
    return () => document.removeEventListener('click', onClick, true);
  }, []);
  // The shell is reused across routes, so the capture dialog closes once its submit has moved to the new page.
  useEffect(() => {
    setQuickCaptureOpen(false);
    setCaptureReq(null);
  }, [location]);
  const quickCaptureAdvisor: QuickAdvisor =
    location === '/strategy' && (advisorParam === 'patent' || advisorParam === 'finance') ? advisorParam : 'market';
  useEffect(() => {
    if (!profile) return;
    const onKey = (e: KeyboardEvent) => {
      if (e.key !== 'n' && e.key !== 'N') return;
      if (e.repeat || e.metaKey || e.ctrlKey || e.altKey || e.shiftKey) return;
      const t = e.target as HTMLElement | null;
      if (t && typeof t.closest === "function" && (t.closest('input, textarea, select, [contenteditable=""], [contenteditable="true"]'))) return;
      // Not while a dialog or sheet is open, or when already capturing.
      if (document.querySelector('[role="dialog"], [aria-modal="true"]') || document.documentElement.classList.contains('spark-modal-open')) return;
      if (location === '/ideas/new') return;
      e.preventDefault();
      analytics.track('capture_open', { source: 'kbd' });
      setQuickCaptureOpen(true);
    };
    window.addEventListener('keydown', onKey);
    return () => window.removeEventListener('keydown', onKey);
  }, [profile, location]);
  // Full-height chat screens own the bottom edge with their composer, so the
  // tab bar steps aside there and the top-bar Menu button opens the sheet.
  const showTabbar = isPhone && !boundMain;

  // Inside one idea: its steps drive the rail (desktop) and the idea bar +
  // Idea map (phone). Kept fresh as the member moves between steps.
  const scopeIdeaId = ideaIdFromPath(location);
  useIdeaScopeSync(scopeIdeaId, location + search);
  const scopeWs = useIdeaWorkspace(isPhone ? scopeIdeaId : null);
  // Capture inside an idea starts on Note, for that idea (the rail already loads this workspace on desktop).
  const captureWs = useIdeaWorkspace(scopeIdeaId);
  const captureIdea = scopeIdeaId ? { id: scopeIdeaId, title: captureWs?.idea.title ?? 'this idea' } : null;
  const [mapOpen, setMapOpen] = useState(false);
  useEffect(() => setMapOpen(false), [location]);
  useEffect(() => {
    if (!isPhone || !scopeIdeaId) return;
    const onOpen = () => setMapOpen(true);
    window.addEventListener('ed:open-idea-map', onOpen);
    return () => window.removeEventListener('ed:open-idea-map', onOpen);
  }, [isPhone, scopeIdeaId]);

  // Top bar height (72px + any status-bar inset), for the pinned page title.
  const [topbarH, setTopbarH] = useState(72);
  useEffect(() => {
    const header = document.querySelector<HTMLElement>('.shell > .topbar');
    if (!header) return;
    const ro = new ResizeObserver(() => setTopbarH(Math.round(header.getBoundingClientRect().height)));
    ro.observe(header);
    return () => ro.disconnect();
  }, []);

  // Page-level hooks for other fixed UI (Feedback pill, save bars) to clear
  // the rail on desktop and the tab bar on phones.
  useEffect(() => {
    const root = document.documentElement;
    root.classList.add('in-app-shell');
    root.classList.toggle('has-tabbar', showTabbar);
    return () => {
      root.classList.remove('in-app-shell', 'has-tabbar');
    };
  }, [showTabbar]);

  // Dismiss the static instant hero (baked into index.html, shown on "/") once
  // the authenticated app shell has mounted. This is the catch for an authed
  // user who lands on "/" and is redirected here: LandingPage never mounts, so
  // its dismiss never fires — without this the overlay would sit over the app
  // until the safety timeout. No-op when the overlay was never shown. See index.html.
  useEffect(() => {
    (window as unknown as { __dismissInstantHero?: () => void }).__dismissInstantHero?.();
  }, []);

  // The full size-44 brand lockup is ~280px wide — on a phone that crowds the
  // account avatar off the right edge (and caused a few px of horizontal
  // overflow). Render a smaller lockup on narrow screens so both fit with a
  // real gutter. Actual size change (not CSS scale) so the layout box shrinks
  // too, and the SVG mark scales with it.
  // Seed from the media query on the FIRST render (lazy initializer) rather than
  // defaulting to false and correcting in an effect — otherwise on a phone the
  // logo paints at size 44, then shrinks to 30 once the effect runs, a layout
  // shift in the top bar on every mobile load. Starting correct means no shift.
  const [compactBrand, setCompactBrand] = useState(
    () => typeof window !== 'undefined' && window.matchMedia('(max-width: 600px)').matches,
  );
  useEffect(() => {
    const mq = window.matchMedia('(max-width: 600px)');
    const apply = () => setCompactBrand(mq.matches);
    apply();
    mq.addEventListener('change', apply);
    return () => mq.removeEventListener('change', apply);
  }, []);

  return (
    <div className={`shell${boundMain ? ' shell--bound' : ''}`}>
      {/* In-app announcement surface (modal is fixed-position; a banner renders
          here, above the top bar). Only shows for a signed-in user. */}
      <AnnouncementSurface authed={!!profile} />
      {/* Phone members from before email was required: add one, once. */}
      <AddEmailPrompt />
      {/* Full-width top bar: the brand lockup sits at the far LEFT (x=48, the
          same gutter as the home/marketing nav), the user cluster at the far
          right. The sidebar + content sit BELOW it, so the bar reads
          consistently with the rest of the app. */}
      <header className="topbar">
        <div style={{ display: 'flex', alignItems: 'center', gap: 16 }}>
          {isPhone && !showTabbar && (
          scopeIdeaId ? (
            // Chat screens inside an idea: the same slot opens this idea's steps.
            <button
              className="shell__sidebar-toggle isc-chatbtn"
              onClick={() => setMapOpen(true)}
              aria-label="This idea's steps"
              aria-haspopup="dialog"
            >
              <span className="isc-mono">{(scopeWs?.idea.title.trim()[0] ?? '').toUpperCase()}</span>
              <span aria-hidden="true">▾</span>
            </button>
          ) : (
          <button
            className="shell__sidebar-toggle"
            onClick={() => setMenuOpen(true)}
            aria-label="Open menu"
            aria-haspopup="dialog"
          >
            <svg width="16" height="16" viewBox="0 0 16 16" fill="none">
              <path d="M2 4h12M2 8h12M2 12h12" stroke="currentColor" strokeWidth="1.5" strokeLinecap="round" />
            </svg>
          </button>
          )
          )}
          <Link href="/dashboard" className="topbar__brand">
            {/* The shell's single brand lockup — home-nav size (44) on desktop,
                a compact 30 on phones so it doesn't crowd the avatar. */}
            <BrandLogo size={compactBrand ? 30 : 44} />
          </Link>
        </div>
        <div className="topbar__right">
          {profile && !isPhone && (
            <Link
              href={captureHref}
              className="tb-capture"
              data-guide="capture-btn"
              title="Capture an idea or a note (N)"
              aria-keyshortcuts="n"
              onClick={(e) => {
                analytics.track('capture_open', { source: 'topbar' });
                e.preventDefault();
                setQuickCaptureOpen(true);
              }}
            >
              + Capture <span className="kbd" aria-hidden="true">N</span>
            </Link>
          )}
          {profile && profile.streakCount > 0 && <StreakIndicator count={profile.streakCount} />}
          {profile && (
            <span className="level-pill" aria-label={`Level ${profile.level}`}>
              {profile.level}
            </span>
          )}
          <AvatarMenu />
        </div>
      </header>

      <div className="shell__body">
      {!isPhone && (
        <aside className="shell__sidebar" aria-label="Primary navigation">
          <SidebarNav ctx={navCtx} isAdmin={isAdmin} roles={profile?.roles ?? []} onNavigate={() => {}} />
        </aside>
      )}

      <div className="shell__main">
        {/* Compact copy of the page's title that pins under the top bar once
            the big header scrolls away. Not on chat screens (own chrome). */}
        {/* Phone, inside an idea: the idea bar names the step, so the
            compact title bar steps aside (never two pinned bars). */}
        {isPhone && scopeIdeaId && !boundMain ? (
          <div style={{ position: 'sticky', top: topbarH, zIndex: 20 }}>
            <IdeaBar ideaId={scopeIdeaId} location={location} onOpenMap={() => setMapOpen(true)} />
          </div>
        ) : (
          !boundMain && <PageTitleBar routeKey={location + search} topOffset={topbarH} />
        )}
        {/* "Scroll for more" cue when the page runs below the fold. */}
        {!boundMain && <ScrollHint routeKey={location + search} />}
        <main
          style={{
            flex: 1,
            display: 'flex',
            flexDirection: 'column',
            // Bounded only for scroll-hosting pages (session chat). Flow pages
            // keep min-height: auto so <main> grows to content and the footer
            // sits after it rather than collapsing up into the overflow.
            ...(boundMain ? { minHeight: 0 } : null),
          }}
        >
          {children}
        </main>
        {/* App screens carry a MINIMAL footer, not the marketing SiteFooter
            (newsletter + link columns + social). The full footer was clutter on
            the dashboard/memo and, on the strategy session, rendered a
            newsletter block directly beneath the message composer.
            Scroll-host pages (session chats, boundMain) own the viewport and
            scroll their message list internally — a footer under the fixed
            composer would bleed across the chat, so it is omitted there. */}
        {!boundMain && <AppFooterMini />}
      </div>
      </div>
      {isPhone && scopeIdeaId && (
        <IdeaMapSheet
          ideaId={scopeIdeaId}
          open={mapOpen}
          onClose={() => setMapOpen(false)}
          onOpenEverything={() => {
            setMapOpen(false);
            setMenuOpen(true);
          }}
        />
      )}
      {/* First-run guide: welcome, "where to click next" spotlight, How to start pill. Finished profiles only. */}
      {profile && (profile.profileCompletedAt || profile.name) && <GuideLayer isPhone={isPhone} boundMain={boundMain} />}
      {/* Shell-level so its fixed overlay isn't trapped inside the top bar. */}
      <QuickStartIdea
        advisor={quickCaptureAdvisor}
        open={quickCaptureOpen}
        onClose={() => {
          setQuickCaptureOpen(false);
          setCaptureReq(null);
        }}
        initialMode={captureReq?.mode ?? (scopeIdeaId ? 'note' : 'idea')}
        ideaCtx={captureIdea}
        prefill={captureReq?.text ?? ''}
        fromNoteId={captureReq?.fromNoteId}
      />
      {isPhone && (
        <MobileNav
          ctx={navCtx}
          isAdmin={isAdmin}
          roles={profile?.roles ?? []}
          hideBar={!showTabbar}
          menuOpen={menuOpen}
          setMenuOpen={setMenuOpen}
          onCapture={() => {
            analytics.track('capture_open', { source: 'tabbar' });
            setQuickCaptureOpen(true);
          }}
        />
      )}
    </div>
  );
}

/**
 * Minimal one-line footer for the authenticated app shell: copyright plus the
 * essential legal/support links, so app screens don't carry the full marketing
 * footer. Quiet and out of the way.
 */
function AppFooterMini() {
  return (
    <footer className="app-footer-mini">
      <span>© {new Date().getFullYear()} EntreDash, Inc.</span>
      <span className="app-footer-mini__links">
        <Link href="/terms">Terms</Link>
        <Link href="/privacy">Privacy</Link>
        <button
          type="button"
          className="app-footer-mini__linkbtn"
          onClick={() => reopenPreferences()}
        >
          Cookies
        </button>
        <Link href="/settings/feedback">Support</Link>
      </span>
    </footer>
  );
}

/**
 * Minimal shell for unauthenticated routes (landing, /try, /signup, /signin)
 */
export function PublicShell({
  children,
  rightAction,
}: {
  children: ReactNode;
  rightAction?: ReactNode;
}) {
  return (
    <div className="pubshell" style={{ display: 'flex', flexDirection: 'column', minHeight: '100dvh' }}>
      {/* The full public navigation, always in its solid (cream) state on
          interior pages so the menu text stays readable over any background.
          A page may still pass a custom rightAction to override Sign in/up. */}
      <MarketingNav variant="solid" rightAction={rightAction} />
      {/* Site-wide floating "← Home" pill: appears on every interior marketing
          page once scrolled past the (non-fixed) nav, so there's always a clear
          way home. Not on the Landing page — that renders its own nav and is
          home already. */}
      <HomePill />
      <main style={{ flex: 1 }}>{children}</main>
      <SiteFooter />
    </div>
  );
}
